import axios from "axios";
import { getServiceURL } from "./utils";

const API = axios.create({
  baseURL: getServiceURL(),
});

// Automatically attach JWT token from localStorage to every request
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // CRITICAL: When config.data is FormData, delete manual Content-Type so Axios/browser generates proper boundary!
    if (config.data instanceof FormData) {
      if (config.headers) {
        delete config.headers["Content-Type"];
        delete config.headers["content-type"];
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Automatically handle 401 Unauthorized (token expired/invalid)
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      // Clear stale session data
      localStorage.removeItem("token");
      localStorage.removeItem("userInfo");
      localStorage.removeItem("storeInfo");

      // Only redirect if user is NOT on a public landing / authentication page
      const publicPaths = ["/", "/home", "", "/login", "/superadmin-login"];
      const currentPath = window.location.pathname.toLowerCase();
      if (!publicPaths.includes(currentPath)) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export const LeadService = {
  submitLead: async (leadData) => {
    return API.post("/lead/submit", leadData);
  },

  getAllLeads: async () => {
    return API.get("/lead/getAll");
  },

  deleteLead: async (id) => {
    return API.delete(`/lead/${id}`);
  },
};

export const DropdownOptionsService = {
  getCuisines: async () => {
    return API.get("/general/cuisines");
  },
  getUserRoles: async () => {
    return API.get("/general/user-roles");
  },
  getUserPermissions: async () => {
    return API.get("/general/user-permissions");
  },
};

export const CommonService = {
  generateQR: async (type, data) => {
    console.log(data);
    return API.post(`/common/generate-qr/${type}`, data, {
      responseType: "arraybuffer",
    });
  },
};

export const AuthService = {
  loginWithEmail: async (email) => {
    return API.post("/user/login", { contact: email });
  },

  superAdminLogin: async (email) => {
    return API.post("/user/superadmin-login", { email });
  },

  verifyOTP: async (email, otp) => {
    return API.post("/user/verify", { contact: email, otp });
  },

  superAdminVerifyOTP: async (email, otp) => {
    return API.post("/user/superadmin-verify", { email, otp });
  },
};

export const UserService = {
  createUser: async (data) => {
    return API.post("/user", data);
  },

  getAllUsersByRestaurantId: async (id) => {
    try {
      const url =
        id && id !== "undefined" && id !== "null" ? `/user/all/${id}` : "/user/all";
      return await API.get(url);
    } catch (err) {
      if (err?.response?.status === 404 && id) {
        return await API.get(`/user/${id}`);
      }
      throw err;
    }
  },

  getUserById: async (id) => {
    return API.get(`/user/${id}`);
  },


  updateUser: async (id, data) => {
    return API.put(`/user/${id}`, data);
  },

  deleteUser: async (id) => {
    return API.delete(`/user/${id}`);
  },

  staffLogin: async (phone, password) => {
    return API.post("/user/staff-login", { phone, password });
  },

  changeStaffPassword: async (userId, newPassword) => {
    return API.patch(`/user/${userId}/change-password`, { newPassword });
  },

  changeStaffPhone: async (userId, newPhone) => {
    return API.patch(`/user/${userId}/change-phone`, { newPhone });
  },
};

export const TableService = {
  createTable: async (data) => {
    return API.post("/tables", data);
  },

  getAllTablesByRestaurantId: async (id) => {
    return API.get(`/tables/all/${id}`);
  },

  getTableById: async (id) => {
    return API.get(`/tables/${id}`);
  },

  updateTable: async (id, data) => {
    return API.put(`/tables/${id}`, data);
  },

  deleteTable: async (id) => {
    return API.delete(`/tables/${id}`);
  },
};

export const MenuService = {
  createMenuItem: async (formData) => {
    return API.post("/menus", formData);
  },

  getAllMenuItems: async (id) => {
    return API.get(`/menus/all/${id}`);
  },

  getMenuItemById: async (id) => {
    return API.get(`/menus/${id}`);
  },

  updateMenuItem: async (id, data) => {
    return API.put(`/menus/${id}`, data);
  },

  deleteMenuItem: async (id) => {
    return API.delete(`/menus/${id}`);
  },

  cloneCommonMenu: async (restaurantId, data) => {
    return API.post(`/menus/clone-common-menu/${restaurantId}`, data);
  },

  revertCommonMenu: async (restaurantId) => {
    return API.delete(`/menus/revert-common-menu/${restaurantId}`);
  },

  updateCategoryAvailability: async (payload) => {
    return API.patch("/menus/category/status", payload);
  },

  deleteCategory: async (restaurantId, category) => {
    return API.delete(`/menus/category/${restaurantId}/${encodeURIComponent(category)}`);
  },

  updateBulkItemAvailability: async (payload) => {
    return API.patch("/menus/bulk/status", payload);
  },
};

export const MasterMenuService = {
  getAllMasterMenuItems: async () => {
    return API.get("/master-menu/all");
  },

  createMasterMenuItem: async (data) => {
    return API.post("/master-menu", data);
  },

  updateMasterMenuItem: async (id, data) => {
    return API.put(`/master-menu/${id}`, data);
  },

  deleteMasterMenuItem: async (id) => {
    return API.delete(`/master-menu/${id}`);
  },

  renameCategory: async (oldCategory, newCategory) => {
    return API.put("/master-menu/category/rename", { oldCategory, newCategory });
  },
};

export const RestaurantService = {
  createRestaurant: async (formData) => {
    return API.post("/restaurants", formData);
  },

  changeLogo: async (restaurantId, formData) => {
    return API.put(`/restaurants/${restaurantId}/change-logo`, formData);
  },

  getAllRestaurants: async () => {
    return API.get("/restaurants");
  },

  getPublicPartners: async () => {
    return API.get("/restaurants/public/partners");
  },

  getRestaurantById: async (restaurantId) => {
    return API.get(`/restaurants/${restaurantId}`);
  },

  updateRestaurant: async (restaurantId, data) => {
    return API.put(`/restaurants/${restaurantId}`, data);
  },

  updateRestaurant_Admin: async (restaurantId, data) => {
    return API.put(`/restaurants/admin/${restaurantId}`, data);
  },

  deleteRestaurant: async (restaurantId) => {
    return API.delete(`/restaurants/${restaurantId}`);
  },

  downloadPrintBridgeZip: async (restaurantId) => {
    return API.get(`/restaurants/download-bridge-zip/${restaurantId}`, {
      responseType: "blob",
    });
  },
};

export const PaymentService = {
  addPaymentQR: async (formData, restaurantId) => {
    return API.post(`/payments/uploadQR/${restaurantId}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  getPaymentQR: async (restaurantId) => {
    return API.get(`/payments/checkQR/${restaurantId}`);
  },
  changePaymentQR: async (formData, restaurantId) => {
    return API.put(`/payments/update/${restaurantId}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  togglePaymentSettings: async (restaurantId, data) => {
    return API.put(`/payments/toggle-settings/${restaurantId}`, data);
  },
  getShopPaymentSummary: async (restaurantId) => {
    return API.get(`/payments/summary/${restaurantId}`);
  },
};

export const BannerService = {
  uploadBanner: async (restaurantId, formData) => {
    return API.post(`/banners/${restaurantId}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  getAllBannersByRestaurantId: async (restaurantId) => {
    return API.get(`/banners/all/${restaurantId}`);
  },

  deleteBanner: async (bannerId) => {
    return API.delete(`/banners/${bannerId}`);
  },
};

export const OrderService = {
  getAllOrders: async (restaurantId) => {
    return API.get(`/orders/all/${restaurantId}`);
  },

  getOrderById: async (id) => {
    return API.get(`/orders/${id}`);
  },

  createOrder: async (restaurantId, data) => {
    return API.post(`/orders/${restaurantId}`, data);
  },

  updateOrder: async (id, data) => {
    return API.put(`/orders/${id}`, data);
  },

  adminEditOrder: async (id, data) => {
    return API.put(`/orders/admin-edit/${id}`, data);
  },

  dispatchKOT: async (orderId, data) => {
    return API.post(`/orders/${orderId}/dispatch-kot`, data);
  },

  dispatchBill: async (orderId, data) => {
    return API.post(`/orders/${orderId}/dispatch-bill`, data);
  },

  updateItemStatus: async (orderId, data) => {
    return API.patch(`/orders/${orderId}/item-status`, data);
  },
};

export const SubscriptionService = {
  createSubscription: async (data) => {
    return API.post(`/subscription`, data);
  },

  getCurrentSubscription: async (restaurantId) => {
    return API.get(`/subscription/${restaurantId}`);
  },

  getAllSubscriptions: async () => {
    return API.get(`/subscription`);
  },

  cancelSubscription: async (subscriptionId) => {
    return API.put(`/subscription/cancel/${subscriptionId}`);
  },

  activateSubscription: async (subscriptionId) => {
    return API.put(`/subscription/activate/${subscriptionId}`);
  },
};

export const ReportService = {
  getShopAnalytics: async (restaurantId, params) => {
    return API.get(`/reports/analytics/${restaurantId}`, { params });
  },

  getMarketPriceBenchmarks: async (restaurantId) => {
    return API.get(`/reports/market-benchmarks/${restaurantId}`);
  },

  getSuperAdminAnalytics: async (params) => {
    return API.get("/reports/superadmin/analytics", { params });
  },

  getStaffActivityReport: async (restaurantId, params) => {
    return API.get(`/reports/staff-activity/${restaurantId}`, { params });
  },
};


